import { Client } from "@/Common/Api/Client";
import { DisplayListUI } from "@/Common/Components/Collections/DisplayListUI";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { APSSPatientDTO } from "@/Patient/Models/Forms/Apss/APSSPatientDTO";
import { FormType } from "@/Patient/Models/Forms/FormTypeEnum";
import { GenericForm } from "@/Patient/Models/Forms/GenericForm";

interface IFormsPopup {
    onCloseCallback: () => void;
    formType: FormType;
    formPopupStrategy: IFormsPopupStrategy;
}

class FormsPopupState {
    public forms: Immutable.List<SearchableItemModelT<GenericForm>>;
    public textSearch: string;
    public isLoading: boolean;
    public title: string;
}

class FormsPopupController extends React.Component<IFormsPopup, FormsPopupState> {
    private id = "formsPopup_id";
    public myleHelper: MyleHelper = new MyleHelper();

    constructor(props: IFormsPopup) {
        super(props);

        this.state = {
            forms: [].toImmutable(),
            isLoading: true,
            textSearch: "",
            title: ""
        };
    }

    public render(): JSX.Element {
        let self = this;

        return (
            <div className="pdf-block">
                <div id={this.id} className="popup pdflist">
                    <div className="p-header ">
                        <a className="close" onClick={this.onCancel.bind(this)}>x</a>
                        <h3 className="thin-font">{cacheStore.GetString("NewLabel")} {this.state.title}</h3>
                    </div>
                    <div className="p-body">
                        {this.state.isLoading == true &&
                            <div>{cacheStore.GetString("LoadingLabel") + "..."}</div>
                        }
                        {this.state.isLoading != true && (
                            <DisplayListUI
                                items={self.state.forms?.toArray() ?? []}
                                classForListContainer="tform-mxh"
                                onSelectedItemCallback={form => this.onFormClick(form)}
                                includeSearchbox={true}
                                renderItem={item => (
                                    <div><a>{item.Value}</a></div>
                                )}
                                classForItem="pdf-form-item"
                                enableFavorite={true}
                                noListBorder={true}
                                onFavoriteItemRemoved={form => this.toggleFavoriteForm(form)}
                                onFavoriteItemAdded={form => this.toggleFavoriteForm(form)}
                            />
                        )}
                    </div>

                    <div className="p-footer">
                        <button
                            className="btn btn-default"
                            onClick={this.onCancel.bind(this)}
                        >
                            {cacheStore.GetString("CancelLabel")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        this.mutateState(s => s.title = this.props.formPopupStrategy.getFormTitle(), () => this.loadThenMutateForms());

        let elem = $("#" + this.id);
        let cnt = document.body;
        let dragOptions = {
            containment: cnt,
            handle: ".p-header",
            stop: function() {
                elem.draggable("destroy");
                window.setTimeout(() => elem.draggable(dragOptions), 80);
            }
        };
        elem.draggable(dragOptions);

        window.setTimeout(() => {
            $("#_searchBar_Form").focus();
        }, 50);

        sessionStore.showGlobalOverlay(true);
    }

    public componentWillUnmount(): void {
        sessionStore.hideGlobalOverlay();
    }

    private mutateState(mutation: (s: FormsPopupState) => void, then: () => void = null): void {
        var s = new FormsPopupState();
        mutation(s);
        this.setState(s, then);
    }

    private onCancel(): void {
        if (this.props.onCloseCallback) {
            this.props.onCloseCallback();
        }
    }

    private toggleFavoriteForm(form: any): void {
        this.props.formPopupStrategy.onFavoriteClick(form, this.mutateForm.bind(this));
    }

    private mutateForm(formCode: string, mutation: (form: SearchableItemModelT<GenericForm>) => void): void {
        this.mutateState(s => {
            s.forms = this.state.forms.toArray().map(f => {
                if (f.Model.formId == formCode) {
                    mutation(f);
                    cacheStore.updateFavoriteFormCache(f.Model);
                }
                return f;
            }).toImmutable();
        });
    }

    private mutateFormList(forms: SearchableItemModelT<GenericForm>[]): void {
        let sortedforms = forms.sortByProperty(x => x.Value).sortByPropertyDesc(x => x.Model.isFavorite);
        this.mutateState(s => {
            s.forms = sortedforms.toImmutable();
            s.isLoading = false;
        });
    }

    private loadThenMutateForms(): void {
        this.props.formPopupStrategy.loadForms(this.mutateFormList.bind(this));
    }

    private onFormClick(form: SearchableItemModelT<GenericForm>): void {
        this.props.formPopupStrategy.onFormItemClick(form);

        window.setTimeout(() => {
            this.props.onCloseCallback();
        }, 20);
    }
}

interface IFormsPopupStrategy {

    loadForms(stateMutatingFn: (forms: SearchableItemModelT<GenericForm>[]) => void);
    onCloseCallback: () => void;
    onFormItemClick(clickedItem: SearchableItemModelT<GenericForm>): void;
    onFavoriteClick(clickedItem: SearchableItemModelT<GenericForm>, stateFormMutationFn: (formCode: string, mutation: (form: SearchableItemModelT<GenericForm>) => void) => void): void;
    getFormTitle(): string;
}

abstract class BaseFormsPopupStrategy implements IFormsPopupStrategy {
    private formType: FormType;

    public onCloseCallback: () => void = null;

    constructor(onCloseCallback: () => void, formType: FormType) {
        this.onCloseCallback = onCloseCallback;
        this.formType = formType;
    }

    public getFormTitle(): string {
        return cacheStore.GetString("FormLabel");
    }

    public abstract loadForms(stateFormListMutatingFn: (forms: SearchableItemModelT<GenericForm>[]) => void);

    public abstract onFormItemClick(clickedItem: SearchableItemModelT<GenericForm>);

    public onFavoriteClick(
        clickedItem: SearchableItemModelT<GenericForm>,
        stateFormMutationFn: (formCode: string, mutation: (form: SearchableItemModelT<GenericForm>) => void) => void
    ): void {
        let formCode = clickedItem.Code;
        let formType = this.formType;

        let requestData = {
            FormType: formType,
            FormId: formCode
        };
        if (!clickedItem.isFavorite) {
            Client.Post(
                ServerOperations.Server.Myle_Forms_AddFavoriteForm,
                requestData,
                response => {
                    let favoriteFormId = response.FavoriteFormId;
                    if (!IsNullOrWhiteSpace(favoriteFormId)) // response == success
                        stateFormMutationFn(formCode, f => {
                            f.Model.isFavorite = true;
                            f.isFavorite = true;
                        });
                }
            );
        }
        else {
            Client.Post(
                ServerOperations.Server.Myle_Forms_RemoveFavoriteForm,
                requestData,
                response => {
                    let isSuccess = response.IsSuccess;
                    if (isSuccess)
                        stateFormMutationFn(formCode, f => {
                            f.Model.isFavorite = false;
                            f.isFavorite = false;
                        });
                }
            );
        }
    }
}

class PdfFormPopupStrategy extends BaseFormsPopupStrategy {
    constructor(onCloseCallback: () => void, formType: FormType) {
        super(onCloseCallback, formType);
    }

    public loadForms(stateFormListMutatingFn: (forms: SearchableItemModelT<GenericForm>[]) => void): void {
        cacheStore.getPDFForms(pdfForms => {
            stateFormListMutatingFn(pdfForms.map(x => GenericForm.fromRadPDFForm(x).toSearchableItemModelT()));
        });
    }

    public onFormItemClick(clickedItem: SearchableItemModelT<GenericForm>): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.CreateRadPdfForm, { formId: clickedItem.Model.formId, form: clickedItem.Model.formName });
    }
}

class ApssFormStrategy extends BaseFormsPopupStrategy {
    private patientId: string;
    private contextSupervisorContactId: string;

    constructor(onCloseCallback: () => void, formType: FormType, patientId: string, contextSupervisorContactId: string = null) {
        super(onCloseCallback, formType);
        this.patientId = patientId;
        this.contextSupervisorContactId = contextSupervisorContactId;
    }

    public async loadForms(stateFormListMutatingFn: (forms: SearchableItemModelT<GenericForm>[]) => void): Promise<void> {
        const apssForms = await ApssClient.fetchAllForms();
        stateFormListMutatingFn(apssForms.map(x => GenericForm.fromAPSSForm(x).toSearchableItemModelT()));
    }

    public onFormItemClick(clickedItem: SearchableItemModelT<GenericForm>): void {
        const patient = APSSPatientDTO.getAPSSPatientDTO(clickedItem.Value, this.patientId, this.contextSupervisorContactId);
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.CreatingAPSSForm, { code: clickedItem.Code, patient: patient });
    }
}

export { ApssFormStrategy, BaseFormsPopupStrategy, FormsPopupController, PdfFormPopupStrategy };
